import {Component, Inject, inject} from '@angular/core';
import {filter, from, map, switchMap, take} from 'rxjs';
import {ActivatedRoute, Router} from '@angular/router';
import {LoadComponent} from '@brunata-angular/ui';
// eslint-disable-next-line @typescript-eslint/naming-convention
import Rollbar from 'rollbar';
import {AuthService, URL_BEFORE_AUTHORIZATION_KEY} from '@brunata-angular/auth';
import {RollbarService} from '@brunata-angular/error-reporter';
import {filterNulls} from '@brunata-angular/util';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {environment} from 'src/environments/environment';
import {Store} from '@ngrx/store';
import {loadUser} from 'src/app/state/actions';
import {selectUser} from 'src/app/state/selectors';
import {UserStateSuccess} from 'src/app/state/reducers';

@Component({
    selector: 'app-auth-redirect-ngonline',
    standalone: true,
    imports: [LoadComponent],
    template: `
        <div class="m-3 text-center">
            <app-load />
        </div>
    `,
})
export class AuthRedirectComponent {
    private activatedRoute = inject(ActivatedRoute);
    private authService = inject(AuthService);
    private router = inject(Router);
    private store = inject(Store);

    constructor(@Inject(RollbarService) rollbar: Rollbar) {
        this.activatedRoute.queryParamMap
            .pipe(
                map(params => {
                    if (
                        // Only works on prod
                        (params.get('error_description')?.startsWith('AADB2C90118') ?? false) ||
                        // For dev and test
                        (!environment.production && params.get('error') === 'server_error')
                    ) {
                        void this.router.navigate(['/reset-password']);
                        return null;
                    } else if (!params.has('code')) {
                        // User is redirected here if e.g. the user cancels the login flow.
                        void this.router.navigate(['/']);
                        return null;
                    }
                    return params.get('code');
                }),
                filterNulls(),
                switchMap(code =>
                    from(this.authService.getAndStoreAccessTokenFromAuthRedirect(code)),
                ),
                switchMap(() => {
                    this.store.dispatch(loadUser());
                    return this.store.select(selectUser).pipe(
                        filter((state): state is UserStateSuccess => state.status === 'success'),
                        take(1),
                    );
                }),
                takeUntilDestroyed(),
            )
            .subscribe({
                next: ({user}) => {
                    if (user.userType === 'CONSUMER') {
                        window.location.replace('/mybrunata');
                        return;
                    }
                    const urlBeforeAuthorization = sessionStorage.getItem(
                        URL_BEFORE_AUTHORIZATION_KEY,
                    );
                    sessionStorage.removeItem(URL_BEFORE_AUTHORIZATION_KEY);
                    if (urlBeforeAuthorization != null) {
                        const url = new URL(urlBeforeAuthorization);
                        void this.router.navigateByUrl(`${url.pathname}${url.search}`);
                    } else {
                        void this.router.navigate(['/']);
                    }
                },
                error: error => {
                    rollbar.error(error);
                    console.error(error);
                    void this.router.navigate(['/']);
                },
            });
    }
}
